/**
 * sectionsExpanded.js — the "which state wins" rule shared by every panel with collapsible
 * SectionLabel subsections (CellSections, MissionPanel).
 *
 * A person's own toggle beats whatever the caller's default computes, until KEY changes underneath
 * them — the identity of what is shown, and (for a surface whose default depends on it) whatever the
 * default is itself a function of. Extracted here, rather than reimplemented on each panel, because
 * that duplication is exactly how CellPanel and MissionPanel have drifted apart before: the same
 * three-line "does the stored state still apply" check written twice is two chances for it to read
 * slightly differently.
 *
 * What each panel owns, and this does not: what goes into its KEY, and what its DEFAULT expansion
 * map is (a cell's depends on draft/editability; a mission's is simply "everything open").
 *
 * `state` is a plain `{ key, expanded }` pair, however the caller stores it (e.g. class component
 * state).
 */

/**
 * The section->expanded map currently in effect: the stored one if it is still keyed to `key`,
 * otherwise `defaultExpanded` (freshly computed by the caller for the current key).
 * @param {{ key?: string|null, expanded?: Object|null }} state
 * @param {string} key
 * @param {Object} defaultExpanded
 * @returns {Object}
 */
export function sectionsExpanded(state, key, defaultExpanded) {
  if (state && state.key === key && state.expanded) return state.expanded;
  return defaultExpanded;
}

/**
 * The next `{ key, expanded }` state after toggling one section — for a caller to hand straight to
 * setState. Reads through sectionsExpanded first, so toggling from a stale key re-seeds from
 * `defaultExpanded` rather than flipping a bit in a map that no longer applies.
 * @param {{ key?: string|null, expanded?: Object|null }} state
 * @param {string} key
 * @param {Object} defaultExpanded
 * @param {string} name - the section being toggled
 * @returns {{ key: string, expanded: Object }}
 */
export function toggleSection(state, key, defaultExpanded, name) {
  const expanded = sectionsExpanded(state, key, defaultExpanded);
  return { key, expanded: { ...expanded, [name]: !expanded[name] } };
}
